import { useEffect, useMemo, useState } from 'react';
import { CircleMarker, MapContainer, Pane, TileLayer, Tooltip, useMapEvents } from 'react-leaflet';
import SegmentToggle from '../common/SegmentToggle';
import NewsModeToggle from '../common/NewsModeToggle';
import type { CoordinateRecord, NewsMode, Segment, TitlesRecord, TopicsBySegment } from '../../types';
import { SEGMENT_COLORS } from '../../constants';
import { fetchTitlesForLocation } from '../../data/titlesStore';

interface TopicsMapProps {
  topics: Record<NewsMode, TopicsBySegment>;
  coordinates: Record<string, CoordinateRecord>;
  newsMode: NewsMode;
  onNewsModeChange: (mode: NewsMode) => void;
  defaultLocation: string;
  week: string;
}

const MapEvents = ({ onZoom }: { onZoom: (zoom: number) => void }) => {
  useMapEvents({
    zoomend(event) {
      onZoom(event.target.getZoom());
    }
  });
  return null;
};

const DETAILS_ZOOM = 9.3;

const TopicsMap = ({
  topics,
  coordinates,
  newsMode,
  onNewsModeChange,
  defaultLocation,
  week
}: TopicsMapProps) => {
  const [segment, setSegment] = useState<Segment>('Left');
  const [zoom, setZoom] = useState(6.3);
  const [selectedLocation, setSelectedLocation] = useState(defaultLocation);
  const [titles, setTitles] = useState<TitlesRecord[]>([]);
  const [loadingTitles, setLoadingTitles] = useState(false);

  const markerData = useMemo(() => {
    const entries = topics[newsMode][segment] ?? [];

    const mapped = entries
      .map((entry) => {
        const topicList = (entry.topics ?? []).slice(0, 3);
        return {
          location: entry.location,
          topics: topicList,
          coord: coordinates[entry.location],
          pageviews: entry.engagement ?? topicList.length
        };
      })
      .filter((entry) => entry.coord);

    return mapped.sort((a, b) => (b.pageviews || 0) - (a.pageviews || 0));
  }, [topics, coordinates, newsMode, segment]);

  useEffect(() => {
    let active = true;
    setLoadingTitles(true);
    fetchTitlesForLocation(selectedLocation, segment, newsMode, week)
      .then((records) => {
        if (!active) return;
        setTitles(records.slice(0, 3));
      })
      .finally(() => {
        if (active) {
          setLoadingTitles(false);
        }
      });

    return () => {
      active = false;
    };
  }, [selectedLocation, segment, newsMode, week]);

  const visibleMarkers = markerData;

  const maxPageviews = useMemo(
    () => Math.max(...markerData.map((entry) => entry.pageviews || 0), 1),
    [markerData]
  );

  const selectedTopics =
    markerData.find((item) => item.location === selectedLocation)?.topics ?? [];

  useEffect(() => {
    if (visibleMarkers.length === 0) {
      return;
    }
    if (!visibleMarkers.some((entry) => entry.location === selectedLocation)) {
      setSelectedLocation(visibleMarkers[0].location);
    }
  }, [visibleMarkers, selectedLocation]);

  const getRadius = (value: number) => {
    if (!value || value <= 0) return 6;
    return 6 + (value / maxPageviews) * 16;
  };

  return (
    <div className="map-section">
      <div className="chart-controls stacked">
        <SegmentToggle value={segment} onChange={setSegment} />
        <NewsModeToggle value={newsMode} onChange={onNewsModeChange} />
      </div>
      <div className="map-wrapper">
        <MapContainer center={[38.1, 23.7]} zoom={6.3} minZoom={5.5} maxZoom={11} scrollWheelZoom>
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          <Pane name="topic-markers" style={{ zIndex: 650 }}>
            {visibleMarkers.map((entry) => {
              const topicsToShow = (entry.topics.length ? entry.topics : ['Τοπικό ενδιαφέρον']).slice(0, 3);
              return (
                <CircleMarker
                  key={`${entry.location}-${segment}`}
                  center={[entry.coord.lat, entry.coord.lng]}
                  radius={getRadius(entry.pageviews)}
                  pathOptions={{
                    color: SEGMENT_COLORS[segment],
                    fillColor: SEGMENT_COLORS[segment],
                    fillOpacity: 0.35,
                    weight: 2
                  }}
                  eventHandlers={{
                    click: () => setSelectedLocation(entry.location)
                  }}
                >
                  <Tooltip
                    pane="topic-tooltips"
                    direction="top"
                    offset={[0, -8]}
                    className="topic-tooltip"
                  >
                    <div>
                      <p className="topic-tooltip__title">{entry.location}</p>
                      <ul>
                        {topicsToShow.map((topic) => (
                          <li key={topic}>{topic}</li>
                        ))}
                      </ul>
                    </div>
                  </Tooltip>
                </CircleMarker>
              );
            })}
          </Pane>
          <Pane name="topic-tooltips" style={{ zIndex: 800 }} />
          <MapEvents onZoom={setZoom} />
        </MapContainer>
      </div>
      <div className="map-side-panel">
        <h3>{selectedLocation}</h3>
        <p className="subtitle">Dominant topics</p>
        <ul>
          {selectedTopics.slice(0, 4).map((topic) => (
            <li key={topic}>{topic}</li>
          ))}
        </ul>
        <div>
          <p className="subtitle">Top titles ({loadingTitles ? 'loading…' : '3'})</p>
          {titles.length === 0 && !loadingTitles && <p className="footnote">No titles available.</p>}
          <ol>
            {titles.map((title) => (
              <li key={title.id}>
                <p className="title-text">{title.title}</p>
                <p className="metrics">Score {Math.round(title.engagementScore)}</p>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </div>
  );
};

export default TopicsMap;
